import { get, isFinite } from 'lodash';

import { EMPLOYMENT_TYPE, FIELD_PATHS, INCOME_TYPE } from '@funnels/constants';
import { AnyFormValues, BusinessIncomeDetails, Company, CompanyOwner, Income } from '@funnels/types';

import formatToPrecision from './formatToPrecision';

export function getSelfEmployedYearlyIncomeAmount(employmentIndex: number = 0, formValues: AnyFormValues) {
  const type = get(formValues, FIELD_PATHS.EMPLOYMENT_TYPE(employmentIndex));
  if (type !== EMPLOYMENT_TYPE.PRIMARY && type !== EMPLOYMENT_TYPE.SECONDARY) {
    return 0;
  }
  const applicantId = get(formValues, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(employmentIndex));
  const companyClientId = get(formValues, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex));
  const company = get(formValues, FIELD_PATHS.COMPANIES(), []).find(
    (company: Company) => get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) === companyClientId
  );
  const companyOwner = get({ companies: [company] }, FIELD_PATHS.COMPANY_OWNERS(0), []).find(
    (companyOwner: CompanyOwner) =>
      get({ companies: [{ owners: [companyOwner] }] }, FIELD_PATHS.COMPANY_OWNER_APPLICANT_ID(0, 0)) === applicantId
  );
  const companyOwnerPercent = get(
    { companies: [{ owners: [companyOwner] }] },
    FIELD_PATHS.COMPANY_OWNER_PERCENT_OWNED(0, 0),
    1
  );
  const companyIncome = get(formValues, FIELD_PATHS.INCOMES(), []).find(
    (income: Income) => get({ incomes: [income] }, FIELD_PATHS.INCOME_COMPANY_CLIENT_ID(0)) === companyClientId
  );
  const companyIncomeType = get({ incomes: [companyIncome] }, FIELD_PATHS.INCOME_TYPE(0));

  // SE full doc
  if (INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC === companyIncomeType) {
    const validCompanyIncomes = get({ incomes: [companyIncome] }, FIELD_PATHS.INCOME_BUSINESS_INCOMES(0), [])
      .sort((i1: any, i2: any) => {
        const amount1 = get({ incomes: [{ incomes: [i1] }] }, FIELD_PATHS.INCOME_BUSINESS_INCOME_YEAR(0, 0), 0);
        const amount2 = get({ incomes: [{ incomes: [i2] }] }, FIELD_PATHS.INCOME_BUSINESS_INCOME_YEAR(0, 0), 0);
        return amount2 - amount1;
      })
      // only count in when there is companyProfitBeforeTax (allow negative number and 0)
      .filter((companyIncome: BusinessIncomeDetails) =>
        isFinite(
          get(
            { incomes: [{ incomes: [companyIncome] }] },
            FIELD_PATHS.INCOME_BUSINESS_INCOME_COMPANY_PROFIT_BEFORE_TAX(0, 0)
          )
        )
      )
      // take latest 2 years
      .slice(0, 2);

    return formatToPrecision(
      validCompanyIncomes.reduce((accum: number, companyIncome: BusinessIncomeDetails) => {
        const profitBeforeTax = get(
          { incomes: [{ incomes: [companyIncome] }] },
          FIELD_PATHS.INCOME_BUSINESS_INCOME_COMPANY_PROFIT_BEFORE_TAX(0, 0),
          0
        );
        const depreciation = get(
          { incomes: [{ incomes: [companyIncome] }] },
          FIELD_PATHS.INCOME_BUSINESS_INCOME_ADD_BACK_DEPRECIATION(0, 0),
          0
        );
        const salary = get(
          { incomes: [{ incomes: [companyIncome] }] },
          FIELD_PATHS.INCOME_BUSINESS_INCOME_ADD_BACK_SALARY(0, 0),
          0
        );
        const nonRecurringExpenses = get(
          { incomes: [{ incomes: [companyIncome] }] },
          FIELD_PATHS.INCOME_BUSINESS_INCOME_ADD_BACK_NON_RECURRING_EXPENSES(0, 0),
          0
        );
        const superannuationExcess = get(
          { incomes: [{ incomes: [companyIncome] }] },
          FIELD_PATHS.INCOME_BUSINESS_INCOME_ADD_BACK_SUPERANNUATION_EXCESS(0, 0),
          0
        );
        const lease = get(
          { incomes: [{ incomes: [companyIncome] }] },
          FIELD_PATHS.INCOME_BUSINESS_INCOME_ADD_BACK_LEASE(0, 0),
          0
        );
        const interest = get(
          { incomes: [{ incomes: [companyIncome] }] },
          FIELD_PATHS.INCOME_BUSINESS_INCOME_ADD_BACK_INTEREST(0, 0),
          0
        );
        const other = get(
          { incomes: [{ incomes: [companyIncome] }] },
          FIELD_PATHS.INCOME_BUSINESS_INCOME_ADD_BACK_OTHER(0, 0),
          0
        );
        const deduction = get(
          { incomes: [{ incomes: [companyIncome] }] },
          FIELD_PATHS.INCOME_BUSINESS_INCOME_DEDUCTION(0, 0),
          0
        );

        const amount =
          profitBeforeTax +
          depreciation +
          salary +
          nonRecurringExpenses +
          superannuationExcess +
          lease +
          interest +
          other -
          deduction;

        return amount * companyOwnerPercent + accum;
      }, 0) / validCompanyIncomes.length
    );
  }

  // SE low doc
  if (INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_LOW_DOC === companyIncomeType) {
    const companyIncomeGross = get({ incomes: [companyIncome] }, FIELD_PATHS.INCOME_BUSINESS_GROSS(0), 0);
    return formatToPrecision(companyIncomeGross * companyOwnerPercent);
  }

  return 0;
}
